import {AssistantType, CollectionType} from '@taopix/taopix-core';
import {BaseUserFlow, UserFlowList, UserFlowStep} from './BaseUserFlow';
import {UserFlowType} from './UserFlowFacade';
import {ROUTE_ASSISTANT} from '../../Constants';
import {CalendarOptions} from '@taopix/taopix-core/dist/types/types';

export class AssistantUserFlow extends BaseUserFlow {
  protected readonly type = UserFlowType.Assistant;

  /**
   * @inheritdoc
   */
  public setNextStep() {
    const nextStep = super.setNextStep();

    if (nextStep[0] === UserFlowStep.None) {
      this.setStep(0);
    }

    return nextStep;
  }

  private getWelcomeScreen = (): UserFlowStep | null => {
    if (
      this.experience.WIZARD.welcomeScreen.showWelcomeScreen &&
      window.location.hash.includes(ROUTE_ASSISTANT)
    ) {
      return UserFlowStep.AssistantWelcome;
    }

    return null;
  };

  private getFrontCoverScreen = (): UserFlowStep | null => {
    const {cover1Step} = this.experience.WIZARD;
    if (!cover1Step.allowCustomersToSelectPicturesAndText) {
      return null;
    }

    return UserFlowStep.AssistantFrontCover;
  };

  private getBackCoverScreen = (): UserFlowStep | null => {
    const {backCoverStep} = this.experience.WIZARD;
    if (!backCoverStep.allowCustomersToSelectPicturesAndText) {
      return null;
    }

    return UserFlowStep.AssistantBackCover;
  };

  private getSmartSelectionScreen = (): UserFlowStep | null => {
    if (this.experience.WIZARD.smartPictureSelectionStep.showSmartPictureSelectionStep) {
      return UserFlowStep.AssistantSmartSelection;
    }

    return null;
  };

  private getPreviewScreen = (): UserFlowStep | null => {
    if (this.experience.WIZARD.previewStep.showPreviewStep) {
      return UserFlowStep.AssistantPreview;
    }

    return null;
  };

  /**
   * @inheritdoc
   */
  public configure(): UserFlowList {
    super.configure();

    const experience = this.experience.WIZARD;

    if (this.productLayout.type === CollectionType.Calendar) {
      return this.configureCalendar();
    }

    switch (experience.designAssistantType.designAssistantType) {
      case AssistantType.Standard: {
        return this.configureStandard();
      }
      case AssistantType.Advanced: {
        return this.configureAdvanced();
      }
      case AssistantType.SmartDesign: {
        return this.configureSmartDesign();
      }
      case AssistantType.PictureSelectionOnly: {
        return this.configurePictureSelection();
      }
    }

    return [];
  }

  /**
   * Returns steps for the Standard Assistant.
   *
   * @returns List of available steps.
   */
  private configureStandard = (): string[] => {
    return this.configureAdvanced();
  };

  /**
   * Returns steps for the Advanced Assistant.
   *
   * @returns List of available steps.
   */
  private configureAdvanced = (): string[] => {
    const steps: UserFlowList = [];

    const welcomeScreen = this.getWelcomeScreen();
    if (null !== welcomeScreen) {
      steps.push(`${this.type}.${welcomeScreen}`);
    }

    steps.push(`${this.type}.${UserFlowStep.AssistantManageAssets}`);

    const frontCover = this.getFrontCoverScreen();
    if (null !== frontCover) {
      steps.push(`${this.type}.${frontCover}`);
    }

    const backCover = this.getBackCoverScreen();
    if (null !== backCover) {
      steps.push(`${this.type}.${backCover}`);
    }

    const previewScreen = this.getPreviewScreen();
    if (null !== previewScreen) {
      steps.push(`${this.type}.${UserFlowStep.AssistantPreview}`);
    }

    return steps;
  };

  /**
   * Returns steps for the Smart Design Assistant.
   *
   * @returns List of available steps.
   */
  private configureSmartDesign = (): string[] => {
    const steps: UserFlowList = [];

    const welcomeScreen = this.getWelcomeScreen();
    if (null !== welcomeScreen) {
      steps.push(`${this.type}.${welcomeScreen}`);
    }

    steps.push(`${this.type}.${UserFlowStep.AssistantManageAssets}`);

    const frontCover = this.getFrontCoverScreen();
    if (null !== frontCover) {
      steps.push(`${this.type}.${frontCover}`);
    }

    const backCover = this.getBackCoverScreen();
    if (null !== backCover) {
      steps.push(`${this.type}.${backCover}`);
    }

    const smartSelection = this.getSmartSelectionScreen();
    if (null !== smartSelection) {
      steps.push(`${this.type}.${smartSelection}`);
    }

    const previewScreen = this.getPreviewScreen();
    if (null !== previewScreen) {
      steps.push(`${this.type}.${UserFlowStep.AssistantPreview}`);
    }

    return steps;
  };

  /**
   * Returns steps for the Smart Design Assistant.
   *
   * @returns List of available steps.
   */
  private configurePictureSelection = (): string[] => {
    const steps: UserFlowList = [`${this.type}.${UserFlowStep.AssistantManageAssets}`];
    return steps;
  };

  /**
   * Returns steps for the Calendar Assistant.
   *
   * @returns List of available steps.
   */
  private configureCalendar = (): string[] => {
    const steps: UserFlowList = [];

    const getCalendarSettings = this.getCalendarSettings();
    const showAddPicturesStep = this.experience.WIZARD.addPicturesStep.showAddPicturesStep;

    if (null !== getCalendarSettings) {
      steps.push(`${this.type}.${UserFlowStep.CalendarSettings}`);
    }

    if (showAddPicturesStep) {
      steps.push(`${this.type}.${UserFlowStep.AssistantManageAssets}`);
    }

    return steps;
  };

  /**
   * Returns the CalendarSettings userFlow step if any settings changes can be made
   *
   * @returns null || UserFlowStep.
   */
  private getCalendarSettings = (): UserFlowStep | null => {
    const calendarOptions = this.designerSettings.project.calendar as CalendarOptions;
    if (
      !calendarOptions.startDayCanChange &&
      !calendarOptions.startMonthCanChange &&
      !calendarOptions.startYearCanChange &&
      !calendarOptions.localeCanChange
    ) {
      return null;
    }
    return UserFlowStep.CalendarSettings;
  };
}
